import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDate } from '@/lib/dates';


interface EmailReportFormProps {
  reportData: any;
  reportType: string;
  onClose?: () => void;
}

const EmailReportForm: React.FC<EmailReportFormProps> = ({
  reportData,
  reportType,
  onClose
}) => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(`${reportType} Report - ${formatDate(new Date())}`);

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSendEmail = async () => {
    if (!email) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        'https://cogajhpxodoryhcbdqwo.supabase.co/functions/v1/891a6e35-7251-4848-9af4-2b3e8e32541d',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            driverEmail: email,
            driverName: reportType,
            shiftData: reportData.shifts || [],
            jobs: reportData.jobs || [],
            customSubject: subject,
            customMessage: message
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Report email sent successfully!'
        });
        if (onClose) onClose();
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send email. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email {reportType} Report
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="email">Recipient Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
          />
        </div>
        
        <div>
          <Label htmlFor="subject">Subject</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>
        
        <div>
          <Label htmlFor="message">Additional Message (Optional)</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add any additional notes..."
            rows={3}
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={handleSendEmail} 
            disabled={isLoading}
            className="flex-1"
          >
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send Email'}
          </Button>
          
          {onClose && (
            <Button 
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailReportForm;